import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  getLoyaltyAnonymousActivity,
  getLoyaltyCampaignPerformance,
  getLoyaltyCustomerActivity,
  getLoyaltyCustomerAnalytics,
  getLoyaltyDevices,
  getLoyaltyGeo,
  getLoyaltyMetrics,
  getLoyaltyProfiles,
  getLoyaltySearchTerms,
  getLoyaltySessions,
  getLoyaltyTopEarners,
  getLoyaltyTopPages,
  setLoyaltyProfileConsent,
} from '../../../gateways/breadstack/loyaltyTrackingApis'
import type {
  LoyaltyConsentInput,
  LoyaltyMetricsRange,
  LoyaltyProfilesFilter,
  LoyaltyProfilesResponse,
} from '../../../types/loyaltyTracking'

const PROFILES_PAGE_SIZE = 10
const ACTIVITY_PAGE_SIZE = 10

// Query keys — the storeId lives in the key so every store's tracking data
// caches independently and invalidation is scoped per store.
export const loyaltyMetricsQueryKey = (
  storeId: string,
  range: LoyaltyMetricsRange,
) => ['loyalty', 'metrics', storeId, range] as const
export const loyaltyProfilesQueryKey = (
  storeId: string,
  search: string,
  filter: LoyaltyProfilesFilter = 'identified',
) => ['loyalty', 'profiles', storeId, search, filter] as const
export const loyaltyProfileSearchQueryKey = (storeId: string, search: string) =>
  ['loyalty', 'profile-search', storeId, search] as const
export const loyaltyCustomerActivityQueryKey = (
  storeId: string,
  customerId: string,
  category?: string,
) =>
  [
    'loyalty',
    'customer-activity',
    storeId,
    customerId,
    category ?? 'all',
  ] as const
export const loyaltyAnonymousActivityQueryKey = (
  storeId: string,
  anonymousId: string,
  category?: string,
) =>
  [
    'loyalty',
    'anonymous-activity',
    storeId,
    anonymousId,
    category ?? 'all',
  ] as const

// ── Analytics / metrics ──────────────────────────────────────────────────────

export const useLoyaltyMetricsQuery = (
  storeId: string,
  range: LoyaltyMetricsRange,
) =>
  useQuery({
    queryKey: loyaltyMetricsQueryKey(storeId, range),
    queryFn: () => getLoyaltyMetrics(storeId, range),
    enabled: Boolean(storeId),
  })

// ── ClickHouse-backed analytics (spec §2.1) ──────────────────────────────────
// One read-only query per analytics endpoint, all keyed by store + range so
// the Tracking tab's range selector drives every card from the same state.

export const loyaltySessionsQueryKey = (
  storeId: string,
  range: LoyaltyMetricsRange,
) => ['loyalty', 'analytics-sessions', storeId, range] as const
export const loyaltyTopEarnersQueryKey = (
  storeId: string,
  range: LoyaltyMetricsRange,
) => ['loyalty', 'analytics-top-earners', storeId, range] as const
export const loyaltySearchTermsQueryKey = (
  storeId: string,
  range: LoyaltyMetricsRange,
) => ['loyalty', 'analytics-search-terms', storeId, range] as const
export const loyaltyTopPagesQueryKey = (
  storeId: string,
  range: LoyaltyMetricsRange,
) => ['loyalty', 'analytics-top-pages', storeId, range] as const
export const loyaltyCampaignPerformanceQueryKey = (
  storeId: string,
  range: LoyaltyMetricsRange,
) => ['loyalty', 'analytics-campaigns', storeId, range] as const
export const loyaltyDevicesQueryKey = (
  storeId: string,
  range: LoyaltyMetricsRange,
) => ['loyalty', 'analytics-devices', storeId, range] as const
export const loyaltyGeoQueryKey = (
  storeId: string,
  range: LoyaltyMetricsRange,
  country?: string,
) => ['loyalty', 'analytics-geo', storeId, range, country ?? 'all'] as const
export const loyaltyCustomerAnalyticsQueryKey = (
  storeId: string,
  customerId: string,
  range: LoyaltyMetricsRange,
) => ['loyalty', 'customer-analytics', storeId, customerId, range] as const

export const useLoyaltySessionsQuery = (
  storeId: string,
  range: LoyaltyMetricsRange,
) =>
  useQuery({
    queryKey: loyaltySessionsQueryKey(storeId, range),
    queryFn: () => getLoyaltySessions(storeId, range),
    enabled: Boolean(storeId),
  })

export const useLoyaltyTopEarnersQuery = (
  storeId: string,
  range: LoyaltyMetricsRange,
  limit = 10,
) =>
  useQuery({
    queryKey: [...loyaltyTopEarnersQueryKey(storeId, range), limit],
    queryFn: () => getLoyaltyTopEarners(storeId, range, limit),
    enabled: Boolean(storeId),
  })

export const useLoyaltySearchTermsQuery = (
  storeId: string,
  range: LoyaltyMetricsRange,
  limit = 20,
) =>
  useQuery({
    queryKey: [...loyaltySearchTermsQueryKey(storeId, range), limit],
    queryFn: () => getLoyaltySearchTerms(storeId, range, limit),
    enabled: Boolean(storeId),
  })

export const useLoyaltyTopPagesQuery = (
  storeId: string,
  range: LoyaltyMetricsRange,
  limit = 20,
) =>
  useQuery({
    queryKey: [...loyaltyTopPagesQueryKey(storeId, range), limit],
    queryFn: () => getLoyaltyTopPages(storeId, range, limit),
    enabled: Boolean(storeId),
  })

export const useLoyaltyCampaignPerformanceQuery = (
  storeId: string,
  range: LoyaltyMetricsRange,
  limit = 5,
) =>
  useQuery({
    queryKey: [...loyaltyCampaignPerformanceQueryKey(storeId, range), limit],
    queryFn: () => getLoyaltyCampaignPerformance(storeId, range, limit),
    enabled: Boolean(storeId),
  })

export const useLoyaltyDevicesQuery = (
  storeId: string,
  range: LoyaltyMetricsRange,
) =>
  useQuery({
    queryKey: loyaltyDevicesQueryKey(storeId, range),
    queryFn: () => getLoyaltyDevices(storeId, range),
    enabled: Boolean(storeId),
  })

// `country` switches the response from per-country rows to per-region rows for
// that country. The country lives in the query key, so the world view and each
// drilled-in country cache independently. `keepPreviousData` keeps the map
// painted while a drill-in loads.
export const useLoyaltyGeoQuery = (
  storeId: string,
  range: LoyaltyMetricsRange,
  country?: string,
) =>
  useQuery({
    queryKey: loyaltyGeoQueryKey(storeId, range, country),
    queryFn: () => getLoyaltyGeo(storeId, range, country),
    enabled: Boolean(storeId),
    placeholderData: keepPreviousData,
  })

// Per-customer analytics header for the Activity dialog (identified profiles
// only — anonymous rows have no customer_id to key on).
export const useLoyaltyCustomerAnalyticsQuery = (
  storeId: string,
  customerId: string,
  range: LoyaltyMetricsRange = '30d',
  options: { enabled?: boolean } = {},
) =>
  useQuery({
    queryKey: loyaltyCustomerAnalyticsQueryKey(storeId, customerId, range),
    queryFn: () => getLoyaltyCustomerAnalytics(storeId, customerId, range),
    enabled:
      Boolean(storeId) && Boolean(customerId) && (options.enabled ?? true),
  })

// ── Profiles list (numbered/offset pagination) ───────────────────────────────
// `cursor` is an opaque integer offset (rows to skip), so 1-based page N maps to
// `cursor = (N - 1) * PROFILES_PAGE_SIZE`. Page-based `useQuery` (one page per
// key) with `keepPreviousData` so switching pages keeps the old rows visible
// instead of flashing empty.

export const useLoyaltyProfilesQuery = (
  storeId: string,
  search: string,
  page: number,
  filter: LoyaltyProfilesFilter = 'identified',
) =>
  useQuery({
    queryKey: [...loyaltyProfilesQueryKey(storeId, search, filter), page],
    queryFn: () =>
      getLoyaltyProfiles(storeId, {
        ...(search ? { search } : {}),
        limit: PROFILES_PAGE_SIZE,
        ...(page > 1
          ? { cursor: String((page - 1) * PROFILES_PAGE_SIZE) }
          : {}),
        filter,
      }),
    enabled: Boolean(storeId),
    placeholderData: keepPreviousData,
  })

// Lightweight one-shot profile search for pickers/autocompletes (e.g. the
// campaign "specific customers" audience). Non-paginated: returns up to
// `limit` matches for the debounced query and only fires once a query exists.
const PROFILE_SEARCH_LIMIT = 10

export const useLoyaltyProfileSearchQuery = (storeId: string, search: string) =>
  useQuery({
    queryKey: loyaltyProfileSearchQueryKey(storeId, search),
    queryFn: () =>
      getLoyaltyProfiles(storeId, {
        search,
        limit: PROFILE_SEARCH_LIMIT,
        // Campaign audience picker never targets opted-out customers, so drop
        // them at the source (the shared Profiles list still shows everyone).
        exclude_unsubscribed: true,
      }),
    enabled: Boolean(storeId) && search.trim().length > 0,
  })

// Optimistic consent flip — the profiles query is now paged (one cache entry
// per page), so match every cached page of the active search (`base` key, no
// page suffix) and flip the matching profile's `subscribed` flag across all of
// them so the row toggles instantly regardless of which page it lives on.
export const useSetLoyaltyProfileConsent = (
  storeId: string,
  search: string,
  filter: LoyaltyProfilesFilter = 'identified',
) => {
  const queryClient = useQueryClient()
  const base = loyaltyProfilesQueryKey(storeId, search, filter)
  return useMutation({
    mutationFn: (input: LoyaltyConsentInput) =>
      setLoyaltyProfileConsent(storeId, input),
    onMutate: async (input: LoyaltyConsentInput) => {
      await queryClient.cancelQueries({ queryKey: base })
      const previous = queryClient.getQueriesData({ queryKey: base })
      queryClient.setQueriesData(
        { queryKey: base },
        (old: LoyaltyProfilesResponse | undefined) =>
          old?.profiles
            ? {
                ...old,
                profiles: old.profiles.map((profile) =>
                  profile.email === input.email
                    ? { ...profile, subscribed: input.subscribed }
                    : profile,
                ),
              }
            : old,
      )
      return { previous }
    },
    onError: (_error, _input, context) => {
      for (const [key, data] of context?.previous ?? []) {
        queryClient.setQueryData(key, data)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: base })
    },
  })
}

// ── Per-customer activity feed (cursor-paginated) ────────────────────────────

export const useLoyaltyCustomerActivityQuery = (
  storeId: string,
  customerId: string,
  category?: string,
  options: { enabled?: boolean } = {},
) =>
  useInfiniteQuery({
    queryKey: loyaltyCustomerActivityQueryKey(storeId, customerId, category),
    queryFn: ({ pageParam }) =>
      getLoyaltyCustomerActivity(storeId, customerId, {
        limit: ACTIVITY_PAGE_SIZE,
        ...(pageParam ? { before: pageParam } : {}),
        ...(category ? { category } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled:
      Boolean(storeId) && Boolean(customerId) && (options.enabled ?? true),
  })

// ── Per-anonymous-session activity feed (cursor-paginated) ───────────────────
// Mirror of useLoyaltyCustomerActivityQuery, keyed by anonymous_id. Powers the
// Activity dialog for anonymous profile rows.

export const useLoyaltyAnonymousActivityQuery = (
  storeId: string,
  anonymousId: string,
  category?: string,
  options: { enabled?: boolean } = {},
) =>
  useInfiniteQuery({
    queryKey: loyaltyAnonymousActivityQueryKey(storeId, anonymousId, category),
    queryFn: ({ pageParam }) =>
      getLoyaltyAnonymousActivity(storeId, anonymousId, {
        limit: ACTIVITY_PAGE_SIZE,
        ...(pageParam ? { before: pageParam } : {}),
        ...(category ? { category } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled:
      Boolean(storeId) && Boolean(anonymousId) && (options.enabled ?? true),
  })
